import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ArrowRight, Filter, X, SlidersHorizontal, Sparkles, Lock, Star } from "lucide-react";
import axios from "axios";
import BookmarkButton from "@/components/BookmarkButton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TOOL_COLORS = {
  "ChatGPT":           "bg-emerald-100 text-emerald-700",
  "Claude":            "bg-orange-100 text-orange-700",
  "Gemini":            "bg-blue-100 text-blue-700",
  "Microsoft Copilot": "bg-sky-100 text-sky-700",
  "Perplexity":        "bg-teal-100 text-teal-700",
};

const TOOLS = ["ChatGPT", "Claude", "Gemini", "Microsoft Copilot", "Perplexity"];
const FUNCTIONS_LIST = [
  { id: "procurement", name: "Procurement" },
  { id: "finance",     name: "Finance" },
  { id: "marketing",   name: "Marketing" },
  { id: "hr",          name: "HR" },
  { id: "sales",       name: "Sales" },
  { id: "operations",  name: "Operations" },
  { id: "general",     name: "General" },
];

const AppCard = ({ app, onClick, isFavourite = false, onToggleFav, isLocked = false }) => {
  const isPremium = app.is_premium;
  const tools = app.recommended_tools || [];
  const variations = app.prompt_variations ? Object.keys(app.prompt_variations) : [];
  const hasVariations = variations.length > 0;

  return (
    <div
      className={`relative bg-white border rounded-xl p-5 hover-lift card-shadow cursor-pointer fade-in transition-all ${
        isLocked ? "border-slate-200 opacity-80 hover:opacity-100" : "border-slate-200"
      }`}
      onClick={onClick}
      data-testid={`browse-app-card-${app.app_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 mr-2">
          <h3 className="font-semibold text-slate-900 text-sm leading-snug flex items-center gap-1.5">
            {isLocked && isPremium && <Lock size={12} className="text-slate-400 flex-shrink-0" />}
            {app.title}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 capitalize">{app.function_id} · {app.category}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Badge className={`text-xs border-0 ${isPremium ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
            {isPremium ? `${app.credit_cost} cr` : "FREE"}
          </Badge>
          {onToggleFav && !isLocked && (
            <BookmarkButton appId={app.app_id} initialState={isFavourite} onToggle={onToggleFav} size={14} />
          )}
        </div>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed line-clamp-2 mb-3">{app.business_context}</p>

      {app.avg_rating > 0 && (
        <div className="flex items-center gap-1.5 mb-3">
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map(i => (
              <Star key={i} size={11} className={i <= Math.round(app.avg_rating) ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"} />
            ))}
          </div>
          <span className="text-xs font-semibold text-slate-600">{app.avg_rating}</span>
          <span className="text-xs text-slate-400">({app.review_count})</span>
        </div>
      )}

      {isLocked && isPremium ? (
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
            <Lock size={11} /> Premium — upgrade required
          </span>
          <ArrowRight size={14} className="text-blue-400" />
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex gap-1 flex-wrap items-center">
            {tools.slice(0, 2).map(t => (
              <span key={t} className={`text-xs px-2 py-0.5 rounded-full font-medium ${TOOL_COLORS[t] || "bg-slate-100 text-slate-500"}`}>
                {t}
              </span>
            ))}
            {tools.length > 2 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                +{tools.length - 2}
              </span>
            )}
            {hasVariations && (
              <span
                className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-medium flex items-center gap-1"
                data-testid={`variations-badge-${app.app_id}`}
              >
                <Sparkles size={9} />
                {variations.length} tool {variations.length === 1 ? "version" : "versions"}
              </span>
            )}
          </div>
          <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
        </div>
      )}
    </div>
  );
};

const BrowsePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedTool, setSelectedTool] = useState(searchParams.get("tool") || "");
  const [selectedFunction, setSelectedFunction] = useState(searchParams.get("function") || "");
  const [selectedPremium, setSelectedPremium] = useState(searchParams.get("premium") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "free_first");
  const [totalCount, setTotalCount] = useState(0);
  const [favouriteIds, setFavouriteIds] = useState([]);

  useEffect(() => {
    fetchApps();
  }, [selectedTool, selectedFunction, selectedPremium, sort]);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/favourites/ids`, { withCredentials: true })
        .then(res => setFavouriteIds(res.data))
        .catch(() => {});
    }
  }, [user]);

  const handleToggleFav = (appId, state) => {
    setFavouriteIds(prev => state ? [...prev, appId] : prev.filter(id => id !== appId));
  };

  const fetchApps = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (selectedTool) params.tool = selectedTool;
      if (selectedFunction && selectedFunction !== "all") params.function_id = selectedFunction;
      if (selectedPremium === "free") params.is_premium = false;
      if (selectedPremium === "premium") params.is_premium = true;
      const res = await axios.get(`${API}/apps`, { params, withCredentials: true });
      let data = res.data;
      if (sort === "free_first") data = [...data].sort((a, b) => a.is_premium - b.is_premium);
      else if (sort === "premium_first") data = [...data].sort((a, b) => b.is_premium - a.is_premium);
      setApps(data);
      setTotalCount(data.length);
    } catch {}
    setLoading(false);
  };

  const filtered = apps.filter(app =>
    !search ||
    app.title.toLowerCase().includes(search.toLowerCase()) ||
    app.category.toLowerCase().includes(search.toLowerCase()) ||
    app.function_id.toLowerCase().includes(search.toLowerCase()) ||
    (app.recommended_tools || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  // Determine lock status per app based on user's plan
  const isAppLocked = (app) => {
    if (!app.is_premium) return false;
    if (!user) return true;
    if (user.subscription_plan === "full") return false;
    const accessible = user.accessible_functions || [];
    if (accessible === "all") return false;
    return !accessible.includes(app.function_id);
  };

  const clearFilter = (filterName) => {
    if (filterName === "tool") setSelectedTool("");
    if (filterName === "function") setSelectedFunction("");
    if (filterName === "premium") setSelectedPremium("");
  };

  const activeFilters = [
    selectedTool && { key: "tool", label: selectedTool },
    selectedFunction && { key: "function", label: FUNCTIONS_LIST.find(f => f.id === selectedFunction)?.name },
    selectedPremium && { key: "premium", label: selectedPremium === "free" ? "Free only" : "Premium only" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Browse AI Applications</h1>
          <p className="text-slate-500 text-sm">
            {selectedTool ? `${filtered.length} applications for ${selectedTool}` : `${filtered.length} applications across all functions`}
          </p>
        </div>

        {/* AI Tool Filter Row */}
        <div className="mb-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Filter by AI Tool</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedTool("")}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                !selectedTool ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              data-testid="tool-filter-all"
            >
              All Tools
            </button>
            {TOOLS.map(tool => (
              <button
                key={tool}
                onClick={() => setSelectedTool(selectedTool === tool ? "" : tool)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  selectedTool === tool
                    ? `${TOOL_COLORS[tool]} border-transparent`
                    : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                }`}
                data-testid={`tool-filter-${tool.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {tool}
              </button>
            ))}
          </div>
        </div>

        {/* Secondary Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search applications..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="browse-search-input"
              />
            </div>
            <Select value={selectedFunction || "all"} onValueChange={v => setSelectedFunction(v === "all" ? "" : v)}>
              <SelectTrigger className="sm:w-44" data-testid="function-filter-select">
                <SelectValue placeholder="All Functions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Functions</SelectItem>
                {FUNCTIONS_LIST.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedPremium || "all"} onValueChange={v => setSelectedPremium(v === "all" ? "" : v)}>
              <SelectTrigger className="sm:w-40" data-testid="premium-filter-select">
                <SelectValue placeholder="All Apps" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Apps</SelectItem>
                <SelectItem value="free">Free Only</SelectItem>
                <SelectItem value="premium">Premium Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="sm:w-44" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="free_first">Free First</SelectItem>
                <SelectItem value="premium_first">Premium First</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-500 py-1">Active filters:</span>
              {activeFilters.map(f => (
                <button
                  key={f.key}
                  onClick={() => clearFilter(f.key)}
                  className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full hover:bg-blue-100 transition-colors"
                >
                  {f.label} <X size={10} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border">
                <Skeleton className="h-5 w-3/4 mb-2" /><Skeleton className="h-4 w-1/2 mb-3" />
                <Skeleton className="h-4 w-full mb-1" /><Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <SlidersHorizontal size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-lg font-medium mb-2">No applications found</p>
            <p className="text-sm mb-4">Try different filters or search terms</p>
            <Button variant="outline" onClick={() => { setSelectedTool(""); setSelectedFunction(""); setSelectedPremium(""); setSearch(""); }}>
              Clear All Filters
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(app => (
              <AppCard
                key={app.app_id}
                app={app}
                isFavourite={favouriteIds.includes(app.app_id)}
                onToggleFav={user ? handleToggleFav : null}
                isLocked={isAppLocked(app)}
                onClick={() => navigate(`/app/${app.app_id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowsePage;
